import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Switch,
  Route,
  Redirect,
  Link,
  matchPath,
  useParams,
  useHistory,
  useLocation,
} from 'react-router-dom';
import type { Location } from 'history';
import { enqueueSnackbar } from 'notistack';
import { GlobalContext } from '../../providers/GlobalProvider';
import RequiredLicense from 'pages/assign/RequiredLicense';
import { LicenseType } from 'pages/assign/LicenseBadge';
import { ImportHubL1, IMPORT_HUB_TABS } from './ImportHubL1';
import type { ImportHubTab } from './ImportHubL1';
import { ImportDatabasePage } from './ImportDatabasePage';
import { ImportProductDetailPage } from './ImportProductDetailPage';
import type {
  RenderProductLink,
  RenderDatabaseLink,
  RenderHrefLink,
} from './routing';
import { setToastHandler } from './lib/toast';
import { ImportHubAccessProvider } from './lib/access';
import './importHub.css';

// Users below this tier keep full VIEW access (browse/open databases, view
// products/product detail, export) but every mutating control is disabled
// with a lock icon. See ImportHubAccessProvider / useImportHubReadOnly.
const IMPORT_HUB_REQUIRED_TIERS = ['business', 'enterprise'];

// The Import Hub pages were built as a self-contained module and emit toasts
// through a host-supplied handler; unwired, that handler only writes to the
// console, so every toast in the tool (screening started/complete, save errors,
// upload failures) was invisible. Bind it to the app's notistack provider once at
// module load, before any page renders.
setToastHandler((level, message) => enqueueSnackbar(message, { variant: level }));

/**
 * Import Hub shell. Adapts the routing-agnostic Import Hub pages (built as a
 * self-contained module) to the app's react-router-dom v5 router. Owns the
 * `.import-hub` style scope (importHub.css scopes every token under it),
 * license gating, the internal /import-hub/* route skeleton, and the
 * <Link>-based link renderers the pages expect as props.
 *
 * Renders inside the app's global Header + NewSidebar — no second chrome.
 * Access mirrors the sibling Export Hub: registered only in local/QA envs
 * (see Routes.tsx) with a self-hiding Business-tier view-only banner.
 */

// Link renderers — hand the app's router <Link> to the router-agnostic pages so
// their internal links do client-side navigation instead of full page loads.
const renderProductLink: RenderProductLink = ({
  id,
  className,
  children,
  onClick,
}) => (
  <Link
    to={`/import-hub/products/${id}`}
    className={className}
    onClick={onClick}
  >
    {children}
  </Link>
);

const renderDatabaseLink: RenderDatabaseLink = ({
  dbId,
  className,
  children,
  onClick,
}) => (
  <Link to={`/import-hub/db/${dbId}`} className={className} onClick={onClick}>
    {children}
  </Link>
);

const renderHrefLink: RenderHrefLink = ({
  href,
  className,
  style,
  children,
  onClick,
}) => (
  <Link to={href} className={className} style={style} onClick={onClick}>
    {children}
  </Link>
);

interface ProductRouteState {
  /** The database page (tab + filters) the product was opened from. */
  returnTo?: string;
}

/** /import-hub/products/:productId — product detail. */
const ProductDetailRoute: React.FC<{ onProductChanged?: () => void }> = ({
  onProductChanged,
}) => {
  const { productId } = useParams<{ productId: string }>();
  const location = useLocation<ProductRouteState | undefined>();
  const returnTo = location.state?.returnTo;
  // A bare /import-hub/db/:dbId opens on the Dashboard tab, so the breadcrumb
  // goes back to the exact view it came from, else to the Products tab.
  const renderDatabaseBackLink: RenderDatabaseLink = ({
    dbId,
    className,
    children,
    onClick,
  }) => {
    const databasePath = `/import-hub/db/${dbId}`;
    const to =
      returnTo &&
      (returnTo === databasePath || returnTo.startsWith(`${databasePath}?`))
        ? returnTo
        : `${databasePath}?tab=products`;
    return (
      <Link to={to} className={className} onClick={onClick}>
        {children}
      </Link>
    );
  };
  return (
    <ImportProductDetailPage
      productId={productId}
      hubHref='/import-hub'
      renderHubLink={renderHrefLink}
      renderDatabaseLink={renderDatabaseBackLink}
      onProductChanged={onProductChanged}
    />
  );
};

/** /import-hub/db/:dbId — database detail. */
const DatabaseRoute: React.FC<{
  dataVersion?: number;
  backgrounded?: boolean;
}> = ({ dataVersion, backgrounded }) => {
  const { dbId } = useParams<{ dbId: string }>();
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const requestedTab = params.get('tab');
  const initialTab = (
    ['dashboard', 'products', 'documents', 'settings'].includes(
      requestedTab ?? '',
    )
      ? requestedTab
      : 'dashboard'
  ) as 'dashboard' | 'products' | 'documents' | 'settings';
  const listParam = (key: string) =>
    params
      .getAll(key)
      .flatMap((value) => value.split(','))
      .map((value) => value.trim())
      .filter(Boolean);
  const initialFilters = {
    q: params.get('q') ?? '',
    risk: listParam('risk'),
    pga: listParam('pga'),
    tariff: listParam('tariff'),
    value: listParam('value'),
    fta: listParam('fta'),
    origin: listParam('origin'),
    addDuty: listParam('add-duty'),
    score: listParam('score'),
    classification: listParam('classification'),
    issues: listParam('issue'),
    hierarchy: listParam('hierarchy'),
    ruleset: listParam('ruleset'),
  };
  const replaceQuery = (
    tab: string,
    filters: typeof initialFilters = initialFilters,
  ) => {
    const next = new URLSearchParams();
    if (tab !== 'dashboard') next.set('tab', tab);
    if (filters.q) next.set('q', filters.q);
    (
      [
        ['risk', filters.risk],
        ['pga', filters.pga],
        ['tariff', filters.tariff],
        ['value', filters.value],
        ['fta', filters.fta],
        ['origin', filters.origin],
        ['add-duty', filters.addDuty],
        ['score', filters.score],
        ['classification', filters.classification],
        ['issue', filters.issues],
        ['hierarchy', filters.hierarchy],
        ['ruleset', filters.ruleset],
      ] as Array<[string, string[]]>
    ).forEach(([key, values]) =>
      values.forEach((value) => next.append(key, value)),
    );
    const search = next.toString();
    const nextLocation = `${location.pathname}${search ? `?${search}` : ''}`;
    const currentLocation = `${location.pathname}${location.search}`;
    if (nextLocation !== currentLocation) {
      history.replace(nextLocation);
    }
  };
  const returnTo = `${location.pathname}${location.search}`;
  const renderProductLinkWithReturn: RenderProductLink = ({
    id,
    className,
    children,
    onClick,
  }) => (
    <Link
      to={{
        pathname: `/import-hub/products/${id}`,
        state: { returnTo } as ProductRouteState,
      }}
      className={className}
      onClick={onClick}
    >
      {children}
    </Link>
  );
  return (
    <ImportDatabasePage
      dbId={dbId}
      initialTab={initialTab}
      initialFilters={initialFilters}
      hubHref='/import-hub'
      renderHubLink={renderHrefLink}
      renderProductLink={renderProductLinkWithReturn}
      dataVersion={dataVersion}
      backgrounded={backgrounded}
      onTabChange={(tab) => replaceQuery(tab)}
      onFiltersChange={(filters) =>
        replaceQuery(initialTab, {
          q: filters.q ?? '',
          risk: filters.risk ?? [],
          pga: filters.pga ?? [],
          tariff: filters.tariff ?? [],
          value: filters.value ?? [],
          fta: filters.fta ?? [],
          origin: filters.origin ?? [],
          addDuty: filters.addDuty ?? [],
          score: filters.score ?? [],
          classification: filters.classification ?? [],
          issues: filters.issues ?? [],
          hierarchy: filters.hierarchy ?? [],
          ruleset: filters.ruleset ?? [],
        })
      }
      onProductCreated={(id) => history.push(`/import-hub/products/${id}`)}
    />
  );
};

/** /import-hub/:tab — Level 1 landing; the active tab lives in the URL. */
const LandingRoute: React.FC = () => {
  const { tab } = useParams<{ tab: string }>();
  const history = useHistory();
  const { cur_user } = useContext(GlobalContext);
  const groupId = cur_user?.selected_team?.id;
  const createdBy = cur_user?.basics?.id;
  // Doc Upload moved to the database-level Documents tab; the homepage tab is
  // now Intelligence · API.
  if (tab === 'documents') {
    return <Redirect to='/import-hub/intelligence' />;
  }
  if (!IMPORT_HUB_TABS.includes(tab as ImportHubTab)) {
    return <Redirect to='/import-hub/databases' />;
  }
  return (
    <ImportHubL1
      initialTab={tab as ImportHubTab}
      onTabChange={(next) => history.push(`/import-hub/${next}`)}
      onProductCreated={(id) => history.push(`/import-hub/products/${id}`)}
      renderProductLink={renderProductLink}
      renderDatabaseLink={renderDatabaseLink}
      renderHrefLink={renderHrefLink}
      groupId={groupId}
      createdBy={createdBy}
    />
  );
};

const PRODUCT_DETAIL_PATH = '/import-hub/products/:productId';

const locationFromPath = (path: string): Location => {
  const queryStart = path.indexOf('?');
  return {
    pathname: queryStart === -1 ? path : path.slice(0, queryStart),
    search: queryStart === -1 ? '' : path.slice(queryStart),
    hash: '',
    state: undefined,
  };
};

/**
 * A product opened from a database page renders over that page instead of
 * replacing it: the database page stays mounted (hidden) at the location it
 * was left on, so going back reuses its loaded rows, filters and page instead
 * of refetching everything. Edits made on the product bump `dataVersion`,
 * which refreshes the hidden page in the background.
 */
const ImportHubRoutes: React.FC = () => {
  const location = useLocation<ProductRouteState | undefined>();
  const [productDataVersion, setProductDataVersion] = useState(0);
  const onProduct = Boolean(
    matchPath(location.pathname, { path: PRODUCT_DETAIL_PATH, exact: true }),
  );
  const returnTo = onProduct ? location.state?.returnTo : undefined;
  const backgroundLocation = useMemo(
    () =>
      returnTo && matchPath(returnTo.split('?')[0], '/import-hub/db/:dbId')
        ? locationFromPath(returnTo)
        : null,
    [returnTo],
  );
  const markProductChanged = useCallback(
    () => setProductDataVersion((n) => n + 1),
    [],
  );

  // Hiding the database page collapses the document, so remember where the
  // table was scrolled and restore it on the way back.
  const hasBackground = Boolean(backgroundLocation);
  const backgroundActiveRef = useRef(hasBackground);
  const listScrollRef = useRef(0);
  useEffect(() => {
    const onScroll = () => {
      if (!backgroundActiveRef.current) listScrollRef.current = window.scrollY;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useLayoutEffect(() => {
    if (backgroundActiveRef.current === hasBackground) return;
    backgroundActiveRef.current = hasBackground;
    window.scrollTo(0, hasBackground ? 0 : listScrollRef.current);
  }, [hasBackground]);

  return (
    <>
      <div style={{ display: backgroundLocation ? 'none' : 'contents' }}>
        <Switch location={backgroundLocation ?? location}>
          {/* Specific (deeper) routes before the single-segment tab catch-all. */}
          <Route
            exact
            path='/import-hub'
            render={() => <Redirect to='/import-hub/databases' />}
          />
          <Route exact path={PRODUCT_DETAIL_PATH}>
            <ProductDetailRoute />
          </Route>
          <Route exact path='/import-hub/db/:dbId'>
            <DatabaseRoute
              dataVersion={productDataVersion}
              backgrounded={hasBackground}
            />
          </Route>
          <Route exact path='/import-hub/:tab' component={LandingRoute} />
          <Redirect to='/import-hub/databases' />
        </Switch>
      </div>
      {backgroundLocation && (
        <Route exact path={PRODUCT_DETAIL_PATH}>
          <ProductDetailRoute onProductChanged={markProductChanged} />
        </Route>
      )}
    </>
  );
};

const ImportHub: React.FC = () => {
  const { cur_user, isWhiteLabel } = useContext(GlobalContext);

  const normalizedLicense = (
    ['none', 'standard', 'pro', 'business', 'enterprise'].includes(
      cur_user?.license,
    )
      ? cur_user.license
      : 'none'
  ) as LicenseType;

  const readOnly =
    !isWhiteLabel &&
    cur_user?.bite_super_admin !== true &&
    !IMPORT_HUB_REQUIRED_TIERS.includes(cur_user?.license);

  return (
    <section className='import-hub'>
      {!isWhiteLabel && (
        <RequiredLicense
          userLicense={normalizedLicense}
          minimumLicense='business'
        />
      )}

      <ImportHubAccessProvider value={readOnly}>
        <ImportHubRoutes />
      </ImportHubAccessProvider>
    </section>
  );
};

export default ImportHub;
